import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { ApiResponse } from '../../models/smartclock/api-response.model';
import { MenuLayout } from '../../../shared/constants/menu';
import { AccessService } from './access.service';

/** Lo que se lee de `GET /access/catalog` para armar el menú (el resto no se usa aquí). */
interface CatalogResponse {
  modules?: { id?: string; label?: string }[];
  sections?: {
    id?: string;
    label?: string;
    module?: string;
    icon?: string | null;
    resources?: { resourceId?: string; label?: string }[];
  }[];
}

/**
 * Estructura del catálogo de permisos para el menú lateral: módulos (Módulo DT, Módulo de Gestión)
 * y secciones, en su orden y con sus nombres (`GET /v1/access/catalog`). El staff la edita desde el
 * backoffice (SMARTCLOCK_API/docs/spec-first/specs/access-catalog-layout.spec.md): renombra,
 * traspasa una sección de módulo o agrupa pantallas en otra sección, y el menú lo sigue.
 *
 * Se pide cuando hay permisos cargados y otra vez cuando `/access/me` trae otro `catalogVersion` o
 * cambia la empresa. Si no carga, `layout()` queda en `null` y el menú usa sus grupos fijos de
 * respaldo (`MENU_GROUPS`): quién ve qué no depende de esto, sólo cómo se agrupa y se llama.
 */
@Injectable({ providedIn: 'root' })
export class CatalogLayoutService {
  private readonly http = inject(HttpClient);
  private readonly access = inject(AccessService);
  private readonly url = `${environment.apiUrl}access/catalog`;

  private readonly _layout = signal<MenuLayout | null>(null);
  readonly layout = this._layout.asReadonly();

  /** Versión del catálogo y empresa de la última petición (evita pedirlo dos veces). */
  private requested: string | null = null;

  constructor() {
    effect(() => {
      const status = this.access.status();
      const version = this.access.catalogVersion();
      const company = this.access.company()?.id ?? '';
      if (status !== 'ready') return;
      const key = `${version ?? ''}|${company}`;
      if (key === this.requested) return;
      this.requested = key;
      untracked(() => this.load(key));
    });
  }

  private load(key: string): void {
    this.http.get<ApiResponse<CatalogResponse>>(this.url).subscribe({
      next: (res) => {
        if (key === this.requested) this._layout.set(toMenuLayout(res?.data));
      },
      error: () => {
        // Se conserva la estructura anterior (o el respaldo); se reintenta con el próximo cambio.
        if (key === this.requested) this.requested = null;
      },
    });
  }
}

/** Respuesta del API → estructura del menú. Sin secciones utilizables: `null` (respaldo). */
export function toMenuLayout(data: CatalogResponse | null | undefined): MenuLayout | null {
  const modules = (data?.modules ?? [])
    .filter((module) => module?.id)
    .map((module) => ({ id: String(module.id), label: String(module.label ?? module.id) }));
  const sections = (data?.sections ?? [])
    .filter((section) => section?.id && section.label)
    .map((section) => ({
      id: String(section.id),
      label: String(section.label),
      module: String(section.module ?? 'general'),
      icon: section.icon ? String(section.icon) : null,
      resources: (section.resources ?? [])
        .filter((resource) => resource?.resourceId)
        .map((resource) => ({
          resourceId: String(resource.resourceId),
          label: String(resource.label ?? resource.resourceId),
        })),
    }));
  return sections.length ? { modules, sections } : null;
}
